import {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react'
import { connect } from 'react-redux'
import {
  DEFAULT_LIMIT_PER_INDEX,
  DISPLAY_PROFILE,
  buildMiniSearchIndices,
  miniSearchIndexSizes,
  searchPerMiniSearchIndex,
} from '../local/buildMiniSearchIndices'
import { searchDebugLog } from '../searchDebugLog'

// Result-object keys, one per content index.
// eslint-disable-next-line camelcase
const FIRST_PAGE = { repos: 1, folders: 1, sets_questions: 1 }
const INDEX_KEYS = Object.keys(FIRST_PAGE)

// An empty query still has to emit the full three-index shape: consumers read
// `indexResults.repos.hitCount` without guarding, so emitting `undefined` here
// throws on mount.
function emptyIndexResults() {
  return INDEX_KEYS.reduce((acc, key) => ({
    ...acc,
    [key]: {
      hits: [], hitCount: 0, hasMore: false, loadMore: () => {},
    },
  }), {})
}

// Which indexes this surface actually presents, in order. The full three-index
// `indexResults` is always emitted for the results report; `hits` and
// `suggestions` are the display projections. On sectionHome only
// sets_questions is presented — concatenating all three there would offer
// packs and folders as pollable items in add-to-class.
//
// Takes the comma-joined secondary list rather than the array so the caller can
// use it as a plain effect dependency.
function presentedIndexes(primaryIndex, secondaryKey) {
  const secondary = secondaryKey ? secondaryKey.split(',') : []
  return [primaryIndex, ...secondary]
}

/**
 * Headless search provider for local Redux-derived library content, powered by
 * MiniSearch (BM25 + token-level prefix/fuzzy).
 *
 * One instance runs per profile, differing only in the `profile` prop: the
 * display instance runs DISPLAY_PROFILE and is what the user sees; each shadow
 * instance runs one SHADOW_PROFILES entry and feeds only the results report.
 * They share the memoised indices, so a shadow costs one extra in-memory BM25
 * pass, and every profile answers the same query over the same library at the
 * same moment.
 *
 * Output shape, rendered by the same `IndexHitList` / `SectionHeader`
 * primitives whatever the profile:
 *   {
 *     query,
 *     suggestions: [{ index, hits }, ...],
 *     hits:        <primary index hits>,
 *     indexResults: {
 *       repos:          { hits, hitCount, hasMore, loadMore, rankedIds },
 *       folders:        { hits, hitCount, hasMore, loadMore, rankedIds },
 *       sets_questions: { hits, hitCount, hasMore, loadMore, rankedIds },
 *     },
 *     indexSizes,
 *   }
 *
 * Each hit copies the source Redux record and adds:
 *   - objectID:            primary key (= record id)
 *   - __minisearchProfile: which profile produced it (e.g. 'tuned-v7')
 *   - __minisearchScore:   BM25 score (higher = stronger match)
 *   - __minisearchTerms:   array of query terms that matched
 *   - __minisearchMatch:   per-term → matched-fields map (MiniSearch `match`)
 */
function MiniSearchContentProvider({
  query, options, onResults, profile,
  sets, questions, folders, repos,
}) {
  const indices = useMemo(
    () => buildMiniSearchIndices({
      sets, questions, folders, repos,
    }),
    [sets, questions, folders, repos],
  )

  const onResultsRef = useRef(onResults)
  onResultsRef.current = onResults
  const queryRef = useRef(query)
  queryRef.current = query

  const indexSizes = useMemo(() => miniSearchIndexSizes(indices), [indices])

  // How many pages deep each index is currently showing, held together with the
  // query it applies to so a new query resets paging without a second effect
  // (which would emit results twice for every keystroke).
  const [paging, setPaging] = useState({ query: null, pages: FIRST_PAGE })
  const pages = paging.query === query ? paging.pages : FIRST_PAGE

  // Show more raises one index's cap. Everything is already in memory, so this
  // re-slices rather than fetching.
  const loadMoreForIndex = useCallback((indexKey) => {
    setPaging((prev) => {
      const current = prev.query === queryRef.current ? prev.pages : FIRST_PAGE
      return {
        query: queryRef.current,
        pages: { ...current, [indexKey]: current[indexKey] + 1 },
      }
    })
  }, [])

  const perPage = (options && options.hitsPerPage) || DEFAULT_LIMIT_PER_INDEX
  const limits = useMemo(
    () => INDEX_KEYS.reduce(
      (acc, key) => ({ ...acc, [key]: perPage * pages[key] }),
      {},
    ),
    [perPage, pages],
  )

  const primaryIndex = (options && options.primaryIndex) || 'repos'
  const secondaryKey = ((options && options.secondaryIndexes) || []).join(',')

  useEffect(() => {
    const presented = presentedIndexes(primaryIndex, secondaryKey)
    if (!query) {
      onResultsRef.current({
        query,
        suggestions: [],
        hits: [],
        indexResults: emptyIndexResults(),
        indexSizes,
      })
      return
    }
    const found = searchPerMiniSearchIndex(indices, query, limits, profile)
    const indexResults = INDEX_KEYS.reduce((acc, key) => ({
      ...acc,
      [key]: { ...found[key], loadMore: () => loadMoreForIndex(key) },
    }), {})
    // Primary index only — the flat list feeds the add-to-class modal, where a
    // hit from another index is not a valid pick.
    const primaryHits = (indexResults[presented[0]] || { hits: [] }).hits
    const suggestions = presented.map((key) => ({
      index: key,
      hits: (indexResults[key] || { hits: [] }).hits,
    }))
    // MiniSearch is synchronous, so the emitted `query` always equals the
    // query prop. The orchestrator still checks it, so a report can never
    // pair one query with another query's results.
    onResultsRef.current({
      query,
      suggestions,
      hits: primaryHits,
      indexResults,
      indexSizes,
    })
    searchDebugLog(`[local-search:minisearch-content:${profile || DISPLAY_PROFILE}]`, {
      query,
      limits,
      profile,
      presented,
      hitCount: primaryHits.length,
      indexSizes,
      indexResults,
      hits: primaryHits,
    })
  }, [
    query, indices, indexSizes, limits, loadMoreForIndex, profile,
    primaryIndex, secondaryKey,
  ])

  return null
}

function mapStateToProps(state) {
  return {
    sets: state.sets,
    questions: state.questions,
    folders: state.folders,
    repos: state.repos,
  }
}

export default connect(mapStateToProps)(MiniSearchContentProvider)
